import { ref } from 'vue'
import { ApiRequestStatus } from '../models/api/api-request-status'
import { useSnackStore } from '@/stores/snack.store'
import { useI18n } from 'vue-i18n'
import { useApiClient } from './api/useApiClient'
import { FetchMethod } from '../models/api/fetch-method'

interface DownloadOptions<TBody> {
  filename?: string
  headers?: Record<string, string>
  method?: FetchMethod
  body?: TBody
  query?: { [key: string]: string | boolean | number | string[] }
}
export function useFileDownload(url: string) {
  const isDownloading = ref(false)
  const { data, status, error, execute, responseHeaders } = useApiClient<Blob>()
  const snackStore = useSnackStore()
  const { t } = useI18n()
  const downloadFile = async <TBody>(options: DownloadOptions<TBody> = {}): Promise<boolean> => {
    const { method = FetchMethod.GET, body, query } = options || {}
    isDownloading.value = true

    await execute({
      method: method,
      endpoint: url,
      omitOrganization: false,
      responseType: 'blob',
      body,
      query,
    })

    if (status.value !== ApiRequestStatus.Success) {
      isDownloading.value = false
      snackStore.showError(t('download.error'))
      return false
    }
    const objectUrl = window.URL.createObjectURL(data.value)

    const link = document.createElement('a')
    link.href = objectUrl
    link.setAttribute(
      'download',
      options.filename ??
        responseHeaders.value?.['content-disposition']?.split('filename=')[1] ??
        'download.pdf',
    )
    document.body.appendChild(link)
    link.click()

    // Clean up to prevent memory leaks
    document.body.removeChild(link)
    window.URL.revokeObjectURL(objectUrl)
    isDownloading.value = false
    return true
  }

  return {
    isDownloading,
    error,
    downloadFile,
  }
}
